import { FC, useEffect, useRef, useState } from 'react';

import { Dialog } from '@mui/material';

import styles from './ContractReportNoticeModal.module.scss';

type Props = {
  open: boolean;
  onConfirm: () => void;
};

export const ContractReportNoticeModal: FC<Props> = ({ open, onConfirm }) => {
  const [hasScrolledToBottom, setHasScrolledToBottom] = useState(false);
  const scrollAreaRef = useRef<HTMLDivElement | null>(null);
  const sentinelRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!open) {
      setHasScrolledToBottom(false);
      return;
    }

    let observer: IntersectionObserver | null = null;

    const raf = requestAnimationFrame(() => {
      const root = scrollAreaRef.current;
      const sentinel = sentinelRef.current;
      if (!root || !sentinel) return;
      observer = new IntersectionObserver(
        (entries) => {
          if (entries.some((entry) => entry.isIntersecting)) {
            setHasScrolledToBottom(true);
          }
        },
        { root },
      );
      observer.observe(sentinel);
    });

    return () => {
      cancelAnimationFrame(raf);
      observer?.disconnect();
    };
  }, [open]);

  return (
    <Dialog
      open={open}
      disableEscapeKeyDown
      fullWidth
      maxWidth="md"
      className={styles.dialog}
      aria-labelledby="contract-report-notice-modal-title"
    >
      <div className={styles.container}>
        <h2 id="contract-report-notice-modal-title" className={styles.title}>
          【重要】成約報告の徹底について
        </h2>

        <div ref={scrollAreaRef} className={styles.scroll_area} tabIndex={0}>
          <p>
            一部加盟店様において、イクラ不動産を通じて取得された売主様情報・対象物件情報に関する成約について、複数件の未報告が確認されました。
          </p>
          <p>
            当該事案については、イクラ不動産利用規約第10条に基づき、サービス利用手数料および違約金をご請求する対応を行っております。
          </p>
          <p>
            イクラ不動産では、本サービスを利用してやり取りを行った査定依頼者との間で、売買成約または媒介等成約があった場合、成約後1週間以内に当社へ報告することをお願いしております。
          </p>
          <p>
            また、報告義務に違反した場合には、規約に基づき、サービス利用手数料に加えて、違約金をご請求する場合があります。
          </p>
          <p>以下について、改めてご確認をお願いいたします。</p>
          <ul className={styles.bullet_list}>
            <li>媒介契約締結時、売買契約締結時、ご決済後は速やかにご報告ください</li>
            <li>成約内容は正確にご申告ください</li>
            <li>過去案件に未報告がないかご確認ください</li>
            <li>未報告案件がある場合は、管理画面内の「運営問合せ」よりご連絡ください</li>
          </ul>
          <p>
            加盟店様との公平なサービス運営および売主様への適切なフォローのため、成約報告の徹底にご協力をお願いいたします。
          </p>
          <div ref={sentinelRef} className={styles.scroll_sentinel} aria-hidden="true" />
        </div>

        <div className={styles.button_container}>
          <button type="button" className={styles.confirm_button} disabled={!hasScrolledToBottom} onClick={onConfirm}>
            確認しました
          </button>
        </div>
      </div>
    </Dialog>
  );
};
